import { ActivatedRoute } from "@angular/router";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { getApp } from "../app";
import { RM_CLIENT } from "../../../../shared/constants/customers";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  getThreadsCompressed,
  searchDataProviderManufacturer
} from "../api.service";
import { Manufacturer } from "../../../../shared/models/manufacturer";
import {
  buildMatchReviewComposedId,
  MatchReviews
} from "../../../../shared/models/matchReviews";
import { Z2DataMatch } from "../../../../shared/models/dataProviders/z2data";
import { ModalComponent } from "../modal/modal.component";
import {
  ExternalDataFilterSuitableService,
  SuitableWorkflowContext
} from "./external-data-filter-suitable.service";

@Component({
    selector: "app-external-data-filter",
    templateUrl: "./external-data-filter.component.html",
    styleUrls: ["./external-data-filter.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ExternalDataFilterComponent implements OnInit, OnChanges {
  readonly matchReasonFieldId = "matchingManufacturer.matchReason";
  @ViewChild("confirmUnsuitableModal", { static: false })
  confirmUnsuitableModal: ModalComponent | undefined;
  @ViewChild("confirmAllUnsuitableModal", { static: false })
  confirmAllUnsuitableModal: ModalComponent | undefined;
  @ViewChild("confirmUnmarkAllSuitableModal", { static: false })
  confirmUnmarkAllSuitableModal: ModalComponent | undefined;
  @ViewChildren("suitableNoteInput")
  suitableNoteInputs: QueryList<ElementRef<HTMLInputElement>> | undefined;

  @Input() manufacturerId: string | null = "";
  @Input() matchReviews: MatchReviews = new MatchReviews();
  @Output() manufacturerStatusChanged: EventEmitter<Manufacturer> =
    new EventEmitter<Manufacturer>();
  @Output() savingStateChanged: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  redoSearch: string | null = null;
  partNumber = "";
  columns: string[] = [];
  columnRows: string[] = [];
  pendingUnsuitableMatch: Z2DataMatch | null = null;
  pendingSuitableChecked = true;
  suitableConfirmModalMessage = "";
  unsuitableNoteModel: { [key: string]: string } = {
    [this.matchReasonFieldId]: ""
  };
  editingSuitableNotePartId: number | null = null;
  suitableNoteDrafts: Record<number, string> = {};
  savingSuitableNotePartIds: Set<number> = new Set<number>();
  isSavingSuitableState = false;

  selectedFilters: { columnName: string; value: string }[] = [];
  resetFilterInputs: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  resetFilterInputsObservable: Observable<boolean> =
    this.resetFilterInputs.asObservable();
  private redoSearchSubscription: Subscription = new Subscription();
  manufacturerPartNumberClean: any = "";
  currentManufacturer: Manufacturer = {} as Manufacturer;
  private hasRequestManufacturerMatches = false;

  constructor(
    private route: ActivatedRoute,
    public suitableService: ExternalDataFilterSuitableService
  ) {}
  app = getApp((app) => {
    this.app = app;
  });

  get showHeaderTitle() {
    return ["matching", "matchingManufacturer"].includes(String(this.app.view));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["matchReviews"] != null &&
      !changes["matchReviews"].firstChange &&
      this.columns.length > 0
    ) {
      this.getColumns();
    }
  }

  async ngOnInit() {
    this.redoSearchSubscription =
      this.app.externalDataFilter.redoSearch.subscribe(async (value) => {
        if (value) {
          this.redoSearch = sessionStorage.getItem("se_filter_cpn");
          if (this.redoSearch !== null) {
            await this.searchManufacturer(this.redoSearch);
          }
          sessionStorage.removeItem("se_filter_cpn");
        }
      });

    // if the se_filter_cpn is set, display the old seach result
    const seFilterCpn = sessionStorage.getItem("se_filter_cpn");
    if (seFilterCpn != null) {
      this.app.manufacturerMatching.manufacturer.matches =
        this.app.externalDataFilter.matchesCopy;
      this.partNumber = seFilterCpn != null ? seFilterCpn : "";
      this.hasRequestManufacturerMatches = false;
      sessionStorage.removeItem("se_filter_cpn");
    }

    /** if a search has been done and you navigate between the pages
     * the "redo search" has to happen again, since they get lost
     */
    this.redoSearch = sessionStorage.getItem("redoSearch");
    if (this.redoSearch !== null) {
      await this.searchManufacturer(this.redoSearch);
    }
    sessionStorage.removeItem("redoSearch");
    const manufacturerIdFromRoute =
      this.route.snapshot.paramMap.get("manufacturer");
    this.manufacturerId = this.manufacturerId || manufacturerIdFromRoute;
    if (this.manufacturerId) {
      this.currentManufacturer =
        await this.app.manufacturerMatching.getCurrentManufacturer(
          this.manufacturerId
        );
      this.hasRequestManufacturerMatches = this.hasManufacturerMatches();
      this.manufacturerPartNumberClean =
        this.getManufacturerPartNumberForReviews(this.currentManufacturer);
    }
    this.getColumns();
    const response = await getThreadsCompressed(
      this.app.customers.expectCurrent,
      "thread"
    );
    this.app.thread.threadsCompressed = response.threads;
    this.app.typeAheadBasic.model["manufacturer.partNumber"] = "";
  }

  /** start the search */
  async searchManufacturer(partNumber: string) {
    this.app.externalDataFilter.searchedPartNumber = partNumber;
    this.hasRequestManufacturerMatches = false;
    this.app.spinner.showSpinner();
    let result: any = null;

    //use dataProviderId in the search if you are on the matching page (in order to add the isAddedproperty to true)
    if (this.app.view === "matching") {
      let dataProviderId = this.app.manufacturerMatching.manufacturer.z2DataId;

      result = await searchDataProviderManufacturer(
        this.app.customers.dataProvider,
        this.app.customers.expectCurrent,
        partNumber,
        dataProviderId
      );
    } else {
      result = await searchDataProviderManufacturer(
        this.app.customers.dataProvider,
        this.app.customers.expectCurrent,
        partNumber
      );
    }

    this.app.externalDataFilter.getSearchResults(result);
    this.getColumns();

    this.resetFilterComponent();
    this.app.spinner.hideSpinner();
  }

  getColumns() {
    this.columns = [...this.app.list.matchingManufacturer.columns];
    this.columnRows = [...this.app.list.matchingManufacturer.columnRows];

    if (this.manufacturerId == null || this.manufacturerId == undefined) {
      this.app.type = "z2DataMatch";
      this.columnRows.splice(
        this.columnRows.findIndex((i) => i === "select"),
        1
      );
      this.columns.splice(
        this.columns.findIndex((i) => i === "select"),
        1
      );
      this.columnRows.push("createCase");
      this.columns.push("createCase");
      if (this.app.customers.getRMCustomers() === RM_CLIENT) {
        this.columns.unshift("createManufacturer");
        this.columnRows.unshift("createManufacturer");
      }
    } else {
      this.app.type = "manufacturer";
    }

    this.hideSuitableColumnsWhenNotRequestMatches();
  }

  showValue(column: string, value: string) {
    if (column === "YEOL" && value === "0.0") {
      return false;
    }
    return true;
  }

  getFilterColumn(column: string) {
    return this.getMatchingResponseColumn(
      this.app.manufacturersFilter.getValuesAsManufacturerKeys(column) || column
    );
  }

  getMatchColumnValue(match: Z2DataMatch, column: string) {
    if (column === "ReachStatus" || column === "REACHStatus") {
      return this.getReachStatus(match);
    }

    const responseColumn = this.getMatchingResponseColumn(column);
    return match[responseColumn] ?? match[column];
  }

  isFilterableColumn(column: string) {
    return (
      column !== "matchReason" &&
      this.app.filterTable.displayFilterTable(column)
    );
  }

  resetAllFilters() {
    this.resetFilterComponent();
    const matches = Array.isArray(this.app.externalDataFilter.matchesCopy)
      ? [...this.app.externalDataFilter.matchesCopy]
      : [];
    this.app.manufacturerMatching.manufacturer.matches = matches;
    this.app.filterTable.getFilterResults(matches, "z2DataMatch");
    this.app.externalDataFilter.resetPagination();
  }

  onFilterResults(resultedDocs: Z2DataMatch[]) {
    this.app.filterTable.getFilterResults(resultedDocs, "z2DataMatch");
    this.app.externalDataFilter.resetPagination();
  }

  private hasManufacturerMatches() {
    return (
      Array.isArray(this.app.manufacturerMatching.manufacturer.matches) &&
      this.app.manufacturerMatching.manufacturer.matches.length > 0
    );
  }

  private hideSuitableColumnsWhenNotRequestMatches() {
    if (this.shouldShowSuitableColumns()) {
      return;
    }

    this.removeColumnPair("suitable", "suitable");
    this.removeColumnPair("matchReason", "MatchReason");
  }

  private shouldShowSuitableColumns() {
    const matchingStatus =
      this.app.manufacturer.getMatchingStatusOfManufacturer(
        this.currentManufacturer
      );

    return (
      this.hasManufacturerMatches() &&
      (matchingStatus === "manual" ||
        this.suitableService.hasAnyNotSuitableReview(this.matchReviews))
    );
  }

  private removeColumnPair(column: string, columnRow: string) {
    this.columns = this.columns.filter(
      (currentColumn) => currentColumn !== column
    );
    this.columnRows = this.columnRows.filter(
      (currentColumnRow) => currentColumnRow !== columnRow
    );
  }

  private getMatchingResponseColumn(column: string) {
    switch (column) {
      case "ReachStatus":
        return "ReachStatus";
      default:
        return column;
    }
  }

  private getReachStatus(match: Z2DataMatch) {
    return (
      match.ReachStatus ||
      match.REACHStatus ||
      match.ReachSatus ||
      match.ComplianceDetails?.ReachStatus ||
      match.ComplianceDetails?.REACHStatus ||
      ""
    );
  }

  async setSuitableValue(match: Z2DataMatch, event: Event) {
    if (this.isSavingSuitableState) {
      return;
    }

    const target = event.target as HTMLInputElement;
    const checked = target.checked;
    const matchPartId = this.suitableService.getMatchPartId(match);
    const result = await this.runWithPersistence(async () => {
      const suitableResult = await this.suitableService.setSuitableValue(
        this.getSuitableWorkflowContext(),
        match,
        checked
      );

      if (suitableResult.action === "applied") {
        await this.syncManufacturerStatus(suitableResult.updatedManufacturer);
      }

      return suitableResult;
    });

    if (result == null) {
      target.checked = !checked;
      return;
    }

    if (result.action === "openConfirmSuitableChange") {
      this.pendingUnsuitableMatch = result.pendingUnsuitableMatch;
      this.pendingSuitableChecked = result.checked;
      this.suitableConfirmModalMessage =
        this.app.text.matchingManufacturer[result.messageKey];
      target.checked = result.revertedChecked;
      this.unsuitableNoteModel[this.matchReasonFieldId] =
        this.suitableService.getInitialSuitableChangeNote(
          this.matchReviews,
          result.pendingUnsuitableMatch,
          result.checked
        );
      this.confirmUnsuitableModal?.open();
      return;
    }

    if (!checked && this.editingSuitableNotePartId === matchPartId) {
      this.resetInlineSuitableNoteState(matchPartId);
    }

  }

  async confirmMarkAsNotSuitable() {
    if (this.isSavingSuitableState) {
      return;
    }

    const pendingUnsuitableMatch = this.pendingUnsuitableMatch;
    const pendingSuitableChecked = this.pendingSuitableChecked;
    const pendingPartId =
      pendingUnsuitableMatch != null
        ? this.suitableService.getMatchPartId(pendingUnsuitableMatch)
        : null;
    const updatedManufacturer = await this.runWithPersistence(async () => {
      const updated = await this.suitableService.confirmMarkAsNotSuitable(
        this.getSuitableWorkflowContext(),
        pendingUnsuitableMatch,
        this.getUnsuitableNote(),
        pendingSuitableChecked
      );
      if (updated != null) {
        await this.syncManufacturerStatus(updated);
      }
      return updated;
    });
    if (updatedManufacturer == null) {
      return;
    }

    this.pendingUnsuitableMatch = null;
    this.pendingSuitableChecked = true;
    this.suitableConfirmModalMessage = "";
    this.resetUnsuitableNote();
    if (
      pendingPartId != null &&
      !pendingSuitableChecked &&
      this.editingSuitableNotePartId === pendingPartId
    ) {
      this.resetInlineSuitableNoteState(pendingPartId);
    }
  }

  async setAllSuitableValue(event: Event) {
    if (this.isSavingSuitableState) {
      return;
    }

    event.stopPropagation();
    const target = event.target as HTMLInputElement;
    const checked = target.checked;
    const result = await this.runWithPersistence(async () => {
      const suitableResult = await this.suitableService.setAllSuitableValue(
        this.getSuitableWorkflowContext(),
        checked
      );

      if (suitableResult.action === "applied") {
        await this.syncManufacturerStatus(suitableResult.updatedManufacturer);
      }

      return suitableResult;
    });

    if (result == null) {
      target.checked = !checked;
      return;
    }

    if (result.action === "none") {
      return;
    }

    if (result.action === "openConfirmAllUnsuitable") {
      target.checked = false;
      this.resetUnsuitableNote();
      this.confirmAllUnsuitableModal?.open();
      return;
    }

    if (result.action === "openConfirmUnmarkAllSuitable") {
      target.checked = true;
      this.resetUnsuitableNote();
      this.confirmUnmarkAllSuitableModal?.open();
      return;
    }

  }

  async confirmMarkAllAsNotSuitable() {
    if (this.isSavingSuitableState) {
      return;
    }

    const updatedManufacturer = await this.runWithPersistence(async () => {
      const updated = await this.suitableService.confirmMarkAllAsNotSuitable(
        this.getSuitableWorkflowContext(),
        this.getUnsuitableNote()
      );
      if (updated != null) {
        await this.syncManufacturerStatus(updated);
      }
      return updated;
    });
    if (updatedManufacturer == null) {
      return;
    }

    this.resetUnsuitableNote();
  }

  async confirmUnmarkAllSuitable() {
    if (this.isSavingSuitableState) {
      return;
    }

    const updatedManufacturer = await this.runWithPersistence(async () => {
      const updated = await this.suitableService.confirmUnmarkAllSuitable(
        this.getSuitableWorkflowContext(),
        this.getUnsuitableNote()
      );
      if (updated != null) {
        await this.syncManufacturerStatus(updated);
      }
      return updated;
    });
    if (updatedManufacturer == null) {
      return;
    }

    this.resetUnsuitableNote();
  }

  isEditingSuitableNote(match: Z2DataMatch) {
    return (
      this.editingSuitableNotePartId ===
      this.suitableService.getMatchPartId(match)
    );
  }

  isSavingSuitableNote(match: Z2DataMatch) {
    return this.savingSuitableNotePartIds.has(
      this.suitableService.getMatchPartId(match)
    );
  }

  startEditingSuitableNote(match: Z2DataMatch) {
    if (!this.suitableService.isSuitableChecked(this.matchReviews, match)) {
      return;
    }

    const partId = this.suitableService.getMatchPartId(match);
    this.editingSuitableNotePartId = partId;
    this.suitableNoteDrafts[partId] = this.suitableService.getSuitableNote(
      this.matchReviews,
      match
    );
    this.focusSuitableNoteInput(partId);
  }

  async saveSuitableNote(match: Z2DataMatch) {
    const partId = this.suitableService.getMatchPartId(match);
    if (!this.isEditingSuitableNote(match)) {
      return;
    }
    if (this.isSavingSuitableNote(match)) {
      return;
    }

    const note = this.suitableNoteDrafts[partId] || "";
    this.savingSuitableNotePartIds.add(partId);
    try {
      const saved = await this.runWithPersistence(async () => {
        await this.suitableService.updateSuitableNote(
          this.getSuitableWorkflowContext(),
          match,
          note
        );
        return true;
      });
      if (saved) {
        this.resetInlineSuitableNoteState(partId);
      }
    } finally {
      this.savingSuitableNotePartIds.delete(partId);
    }
  }

  private async runWithPersistence<T>(
    operation: () => Promise<T>
  ): Promise<T | undefined> {
    if (this.isSavingSuitableState) {
      return undefined;
    }

    this.isSavingSuitableState = true;
    this.savingStateChanged.emit(true);
    this.app.spinner.showSpinner();

    try {
      return await operation();
    } catch (error) {
      console.error("Could not persist match-review changes.", error);
      this.app.hasError = true;
      this.app.errorText =
        error instanceof Error
          ? error.message
          : "The match-review changes could not be saved. Please try again.";
      return undefined;
    } finally {
      this.app.spinner.hideSpinner();
      this.isSavingSuitableState = false;
      this.savingStateChanged.emit(false);
    }
  }

  private getSuitableWorkflowContext(): SuitableWorkflowContext {
    return {
      customer: this.app.customers.expectCurrent,
      composedID: buildMatchReviewComposedId(
        this.currentManufacturer.partNumber,
        this.currentManufacturer.manufacturerPartNumberRaw,
        this.currentManufacturer.nameRaw
      ),
      manufacturerPartNumberClean: this.manufacturerPartNumberClean,
      matchReviews: this.matchReviews,
      currentManufacturer: this.currentManufacturer,
      manufacturerMatches:
        this.app.manufacturerMatching.manufacturer.matches || []
    };
  }

  private async syncManufacturerStatus(
    updatedManufacturer: Manufacturer | null
  ) {
    const persistedManufacturer =
      await this.suitableService.persistManufacturerStatus(
        this.currentManufacturer,
        updatedManufacturer,
        (nextManufacturer: Manufacturer, currentManufacturer?: Manufacturer) =>
          this.app.manufacturerMatching.updateManufacturer(
            nextManufacturer,
            currentManufacturer
          )
      );

    if (persistedManufacturer == null) {
      return;
    }

    this.currentManufacturer = persistedManufacturer;
    this.app.manufacturerMatching.manufacturer = persistedManufacturer;
    this.getColumns();
    this.manufacturerStatusChanged.emit(persistedManufacturer);
  }

  private getUnsuitableNote() {
    const note = this.unsuitableNoteModel[this.matchReasonFieldId];
    return typeof note === "string" ? note.trim() : "";
  }

  private resetUnsuitableNote() {
    this.unsuitableNoteModel[this.matchReasonFieldId] = "";
  }

  private resetInlineSuitableNoteState(partId: number) {
    this.editingSuitableNotePartId = null;
    delete this.suitableNoteDrafts[partId];
  }

  private focusSuitableNoteInput(partId: number) {
    setTimeout(() => {
      const noteInput = this.suitableNoteInputs?.find((input) => {
        return (
          Number(input.nativeElement.getAttribute("data-part-id")) === partId
        );
      });
      if (!noteInput) {
        return;
      }

      noteInput.nativeElement.focus();
      noteInput.nativeElement.select();
    }, 0);
  }

  resetFilterComponent() {
    this.selectedFilters = [];
    this.app.externalDataFilter.resetPagination();
    this.resetFilterInputs.next(true);
  }

  private getManufacturerPartNumberForReviews(manufacturer: Manufacturer) {
    return manufacturer.manufacturerPartNumberRaw || "";
  }

  showDefaultBtn(manufacturer: Manufacturer, match: Z2DataMatch) {
    return manufacturer.z2DataId !== match.PartID.toString();
  }

  ngOnDestroy(): void {
    this.app.manufacturerMatching.manufacturer.matches = [];

    if (this.redoSearchSubscription) {
      this.redoSearchSubscription.unsubscribe();
    }
  }
}
